import { Parameter } from 'shapediver-types';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { isEqual, flatten } from 'lodash';
import { AppThunk } from '../../store';
import { ParameterType, ShapeDiverValue } from '../../shape-diver';
import { changeValues } from './values-slice';
import { unsafeCast } from '../../utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type ParameterDefinition = Parameter.ParameterDefinition;

export type PotterwareFile = {
  date: string;
  ticketId: string;
  definitions: ParameterDefinition[];
  values: Record<string, ShapeDiverValue>;
};

export function saveValues(fileName: string): AppThunk {
  return function (dispatch, getState) {
    const { model, values } = getState();
    const { parameters } = model;

    const data: PotterwareFile = {
      date: moment().format(),
      ticketId: PW.config.ticketId,
      definitions: parameters,
      values,
    };

    const blob = new Blob([JSON.stringify(data, undefined, 2)], {
      type: 'text/json;charset=utf-8',
    });

    saveAs(blob, `${fileName}.pwr`);
  };
}

export function loadValues(file: File): AppThunk {
  async function parseFile(): Promise<PotterwareFile> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      if (!file) return;

      reader.readAsText(file);
      reader.onload = (): void => {
        const potterwareFile = JSON.parse(
          reader.result as string
        ) as PotterwareFile;
        resolve(potterwareFile);
      };

      reader.onerror = reject;
    });
  }

  function filterConfiguredInputs(file: PotterwareFile): PotterwareFile {
    const { leftPanel, downloadPanel, profilePanel, uploadPanel } = PW.config;
    const configuredInputs = flatten([
      flatten(leftPanel.map((p) => p.inputs)),
      downloadPanel.fileNameInput,
      profilePanel.inputs,
      uploadPanel.inputs,
    ]);
    const configuredInputsNames = configuredInputs.map((p) =>
      typeof p === 'string' ? p : p.name
    );

    const filteredDefinitions = file.definitions.filter(
      (d) => configuredInputsNames.indexOf(d.name || '') !== -1
    );
    const filteredValues = filteredDefinitions
      .map((d) => {
        const id = d.id as string;
        return { [id]: file.values[id] };
      })
      .reduce((acc, cur) => ({ ...acc, ...cur }), {});

    return {
      ...file,
      definitions: filteredDefinitions,
      values: filteredValues,
    };
  }

  return async function (dispatch, getState) {
    try {
      const pFile = filterConfiguredInputs(await parseFile());
      // const pFile = await parseFile();

      if (PW.config.ticketId === pFile.ticketId) {
        dispatch(changeValues(pFile.values));
      } else {
        const modelDefinitions = getState().model.parameters;

        const findDefinition = (
          name: string
        ): ParameterDefinition | undefined => {
          return modelDefinitions.find((d) => d.name === name);
        };

        const currentModelValue = (fd: ParameterDefinition) => {
          const md = findDefinition(unsafeCast(fd.name));

          // There is no such definition in the model, so the parameter
          // has either been removed or renamed.
          if (!md) return;

          // The type of the definition has changed, so the value from the file
          // does not make any sense.
          if (md.type !== fd.type) return;

          // Lets see if the selected choice in the file is still applicable for
          // string lists.
          if (
            md.type === ParameterType.STRINGLIST &&
            !isEqual(md.choices, fd.choices)
          ) {
            const value = pFile.values[fd.id as string];
            const fileChoice = (fd.choices || [])[value as number];
            const mdValue = (md.choices || []).indexOf(fileChoice);
            const choiceExists = mdValue !== -1;

            if (!choiceExists) return;

            return { id: md.id, value: mdValue as ShapeDiverValue };
          }

          const isNumberInput =
            [
              ParameterType.INT,
              ParameterType.ODD,
              ParameterType.EVEN,
              ParameterType.FLOAT,
            ]
              .map(String)
              .indexOf(md.type || '') !== -1;

          if (isNumberInput) {
            const value = pFile.values[fd.id as string] as number;
            const min = md.min ? Number(md.min) : Number.MIN_VALUE;
            const max = md.max ? Number(md.max) : Number.MAX_VALUE;

            if (value <= min || value >= max) return;
          }

          return {
            id: md.id,
            value: pFile.values[fd.id as string] as ShapeDiverValue,
          };
        };

        const applicableValues = pFile.definitions
          .map(currentModelValue)
          .filter((c) => c !== undefined)
          .reduce((acc, cur) => {
            if (cur !== undefined)
              return { ...acc, [cur.id as string]: cur?.value };
            else return acc;
          }, {});

        dispatch(changeValues(applicableValues));
      }

      dispatch(
        fileLoaded({ name: file.name + new Date().getTime().toString() })
      );
    } catch (e) {
      console.error('Error parsing file', e);
    }
  };
}

// XXX: This is used only to reset the input initial values. Not very good idea,
// need to find better way ... Goot enough for the moment.
const potterwareFileSlice = createSlice({
  name: 'potterwareFile',
  initialState: 'none',
  reducers: {
    fileLoaded(state, action: PayloadAction<{ name: string }>) {
      return action.payload.name;
    },
  },
});

const { fileLoaded } = potterwareFileSlice.actions;
const potterwareFileReducer = potterwareFileSlice.reducer;

export { fileLoaded, potterwareFileReducer };
